import { memo, useMemo, useCallback, useState, useEffect } from 'react';
import { getCategoryConfig } from '../config/categoryConfigs';

/**
 * Category UI Components and Performance Hooks
 * Reusable components for category pages with built-in performance optimizations
 */

// Memoized category card component
export const CategoryCard = memo(({ 
  categoryType, 
  onNavigate, 
  className = '',
  showDescription = true,
  showFeatures = false 
}) => {
  const config = useMemo(() => getCategoryConfig(categoryType), [categoryType]);
  
  const handleClick = useCallback(() => {
    if (onNavigate && config) {
      onNavigate(`/services/categories/${config.name}`);
    }
  }, [onNavigate, config]);

  if (!config) return null;

  return (
    <div 
      className={`category-card cursor-pointer transition-all duration-300 hover:shadow-lg ${className}`}
      onClick={handleClick}
      style={{
        '--primary-color': config.getCustomization('primaryColor'),
        '--secondary-color': config.getCustomization('secondaryColor')
      }}
    >
      <div className="category-card__header">
        <div className="category-card__icon text-3xl mb-2">
          {config.icon}
        </div>
        <h3 className="category-card__title font-semibold text-lg">
          {config.displayName}
        </h3>
      </div>
      
      {showDescription && (
        <div className="category-card__description text-sm text-gray-600 mt-2">
          {config.description}
        </div>
      )}
      
      {showFeatures && (
        <div className="category-card__features mt-3">
          <div className="flex flex-wrap gap-1">
            {config.features.slice(0, 3).map(feature => (
              <span 
                key={feature}
                className="text-xs px-2 py-1 bg-gray-100 rounded-full"
              >
                {feature.replace(/_/g, ' ').toLowerCase()}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
});

CategoryCard.displayName = 'CategoryCard';

// Memoized service card component
export const ServiceCard = memo(({ 
  service, 
  onBookNow, 
  onLearnMore, 
  onToggleFavorite,
  isFavorite = false,
  showActions = true,
  layout = 'grid' // 'grid' or 'list'
}) => {
  const handleBookNow = useCallback(() => {
    onBookNow?.(service);
  }, [onBookNow, service]);

  const handleLearnMore = useCallback(() => {
    onLearnMore?.(service);
  }, [onLearnMore, service]);

  const handleToggleFavorite = useCallback((e) => {
    e.stopPropagation();
    onToggleFavorite?.(service.id);
  }, [onToggleFavorite, service.id]);

  const cardClassName = useMemo(() => {
    const baseClasses = 'service-card bg-white rounded-lg border border-gray-200 transition-all duration-300 hover:shadow-md';
    return layout === 'list' 
      ? `${baseClasses} flex items-center space-x-4 p-4`
      : `${baseClasses} p-6`;
  }, [layout]);

  return (
    <div className={cardClassName}>
      {/* Service Image */}
      <div className={`service-card__image ${layout === 'list' ? 'w-20 h-20' : 'w-full h-48'} bg-gray-100 rounded-lg mb-4 flex items-center justify-center text-4xl`}>
        {service.image || '🕉️'}
      </div>

      {/* Service Content */}
      <div className={`service-card__content ${layout === 'list' ? 'flex-1' : ''}`}>
        <div className="flex justify-between items-start mb-2">
          <h3 className="service-card__title font-semibold text-lg text-gray-900">
            {service.title}
          </h3>
          {onToggleFavorite && (
            <button
              onClick={handleToggleFavorite}
              className="service-card__favorite text-2xl hover:scale-110 transition-transform"
              aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
            >
              {isFavorite ? '❤️' : '🤍'}
            </button>
          )}
        </div>

        <p className="service-card__description text-gray-600 text-sm mb-3 line-clamp-2">
          {service.description}
        </p>

        <div className="service-card__meta flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2 text-sm text-gray-500">
            <span>⭐ {service.rating}</span>
            <span>•</span>
            <span>{service.reviewCount} reviews</span>
            <span>•</span>
            <span>{service.duration}</span>
          </div>
          <div className="service-card__price font-bold text-lg text-gray-900">
            ₹{service.price?.toLocaleString()}
          </div>
        </div>

        {/* Features */}
        {service.features && service.features.length > 0 && (
          <div className="service-card__features mb-4">
            <div className="flex flex-wrap gap-1">
              {service.features.slice(0, 3).map(feature => (
                <span 
                  key={feature}
                  className="text-xs px-2 py-1 bg-blue-50 text-blue-600 rounded-full"
                >
                  {feature.replace(/_/g, ' ').toLowerCase()}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        {showActions && (
          <div className="service-card__actions flex space-x-2">
            <button
              onClick={handleLearnMore}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
            >
              Learn More
            </button>
            <button
              onClick={handleBookNow}
              className="flex-1 px-4 py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700 transition-colors text-sm font-medium"
            >
              Book Now
            </button>
          </div>
        )}
      </div>
    </div>
  );
});

ServiceCard.displayName = 'ServiceCard';

// Virtualized service list for performance with large datasets
export const VirtualizedServiceList = memo(({ 
  services, 
  onBookNow, 
  onLearnMore,
  onToggleFavorite,
  favorites = [],
  layout = 'grid',
  itemHeight = 300,
  containerHeight = 600
}) => {
  // Implementation would use react-window or react-virtualized
  // For now, we'll use a simple implementation
  const visibleServices = useMemo(() => {
    // In a real implementation, this would calculate visible items
    return services;
  }, [services]);

  return (
    <div 
      className={`virtualized-service-list ${layout === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' : 'space-y-4'}`}
      style={{ height: containerHeight }}
    >
      {visibleServices.map(service => (
        <ServiceCard
          key={service.id}
          service={service}
          onBookNow={onBookNow}
          onLearnMore={onLearnMore}
          onToggleFavorite={onToggleFavorite}
          isFavorite={favorites.includes(service.id)}
          layout={layout}
        />
      ))}
    </div>
  );
});

VirtualizedServiceList.displayName = 'VirtualizedServiceList';

// Performance monitoring hook
export const usePerformanceMonitor = (componentName) => {
  const startTime = useMemo(() => performance.now(), []);
  
  return useCallback(() => {
    const endTime = performance.now();
    const renderTime = endTime - startTime;
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`${componentName} render time: ${renderTime.toFixed(2)}ms`);
    }
    
    // In production, you could send this to analytics
    return renderTime;
  }, [componentName, startTime]);
};

// Debounced search hook
export const useDebouncedSearch = (searchFn, delay = 300) => {
  const debouncedFn = useMemo(() => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => searchFn(...args), delay);
    };
  }, [searchFn, delay]);

  return debouncedFn;
};

// Intersection observer hook for lazy loading
export const useIntersectionObserver = (ref, options = {}) => {
  const [isIntersecting, setIsIntersecting] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => setIsIntersecting(entry.isIntersecting),
      options
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [ref, options]);

  return isIntersecting;
};
